'use client';

import { useState, useRef, useCallback, Suspense } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Container from '@/components/ui/container';
import { submitContactAction } from '@/app/actions/contact';
import { Loader2, ShieldCheck } from 'lucide-react';
import AltchaWidget, { AltchaErrorDetail } from '@/components/forms/AltchaWidget';
import DatePicker from '@/components/forms/DatePicker';
import ConsumerSuccessPage from '@/components/forms/QuoteForm/ConsumerSuccessPage';
import { useSearchParams } from 'next/navigation';
import { E2E_BYPASS_KEY, E2E_BYPASS_TOKEN, isE2EBypass } from '@/lib/e2e-bypass';
import { trackEvent } from '@/lib/analytics';
import { clientLogger } from '@/lib/client-logger';
import { serializeError } from '@/lib/serialize-error';

import { contactFormSchema, ContactFormSchema } from './schema';

const FORM_NAME = 'showroom_afspraak';

function ContactFormContent() {
	const searchParams = useSearchParams();

	// E2E bypass: skip ALTCHA when ?_e2e=BREUER_E2E_2024
	const e2eBypass = isE2EBypass(searchParams.get(E2E_BYPASS_KEY));

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [leadId, setLeadId] = useState<string | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);
	const [altchaPayload, setAltchaPayload] = useState<string | null>(
		e2eBypass ? E2E_BYPASS_TOKEN : null,
	);
	const formRef = useRef<HTMLDivElement>(null);
	const hasTrackedStart = useRef(false);

	const methods = useForm<ContactFormSchema>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			contact_name: '',
			contact_email: '',
			contact_phone: '',
			preferred_date: '',
			preferred_time_slot: undefined,
			interest: '',
			privacy_agreed: false,
		},
		mode: 'onSubmit',
	});

	const {
		register,
		formState: { errors },
		setValue,
		watch,
		handleSubmit,
	} = methods;

	const privacyAgreed = watch('privacy_agreed');
	const preferredDate = watch('preferred_date');

	// Track first interaction with the form
	const handleFormStart = () => {
		if (!hasTrackedStart.current) {
			hasTrackedStart.current = true;
			trackEvent('form_start', { form_name: FORM_NAME });
		}
	};

	const handleAltchaError = useCallback((detail: AltchaErrorDetail) => {
		clientLogger.error('ALTCHA widget error', {
			stage: detail.stage,
			form: FORM_NAME,
			...serializeError(detail.error),
		});
	}, []);

	const onSubmit = async (data: ContactFormSchema) => {
		const requestId = crypto.randomUUID();

		if (!altchaPayload && !e2eBypass) {
			clientLogger.warn('Form submit blocked: ALTCHA not ready', { form: FORM_NAME, request_id: requestId });
			setError('Spam verificatie nog niet voltooid. Wacht even en probeer opnieuw.');

return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const formData = new FormData();

			// Append ALTCHA token
			formData.append('altcha', altchaPayload || E2E_BYPASS_TOKEN);
			formData.append('_request_id', requestId);

			// E2E bypass flag for server-side
			if (e2eBypass) {
				formData.append('_e2e_bypass', 'true');
			}

			// Device metadata for lead enrichment
			formData.append('_device_ua', navigator.userAgent || '');
			formData.append('_device_screen', `${window.innerWidth}x${window.innerHeight}`);
			formData.append(
				'_device_type',
				window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
			);
			formData.append('_device_language', navigator.language || 'unknown');

			// Append form fields
			Object.entries(data).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					formData.append(key, String(value));
				}
			});

			const result = await Promise.race([
				submitContactAction({ success: false }, formData),
				new Promise<never>((_resolve, reject) =>
					setTimeout(() => reject(new Error('SUBMIT_TIMEOUT')), 30_000),
				),
			]);

			if (result.success) {
				trackEvent('form_submit_success', {
					form_name: 'showroom_afspraak',
				});
				setLeadId(result.leadId);
				setIsSuccess(true);

				// Scroll to top of form
				if (formRef.current) {
					const rect = formRef.current.getBoundingClientRect();
					const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
					window.scrollTo({
						top: rect.top + scrollTop - 100,
						behavior: 'smooth',
					});
				}
			} else {
				trackEvent('form_submit_error', {
					form_name: 'showroom_afspraak',
					error_type: 'server_validation',
				});
				setError(result.message || 'Er is iets misgegaan. Probeer het opnieuw.');
				setAltchaPayload(null);
			}
		} catch (err) {
			const isTimeout = err instanceof Error && err.message === 'SUBMIT_TIMEOUT';
			clientLogger.error('Contact form submission failed', {
				error_type: isTimeout ? 'timeout' : 'exception',
				form: FORM_NAME,
				request_id: requestId,
				...serializeError(err),
			});
			trackEvent('form_submit_error', {
				form_name: 'showroom_afspraak',
				error_type: isTimeout ? 'timeout' : 'catch_error',
			});
			setError(
				isTimeout
					? 'Het verzenden duurt te lang. Controleer uw internetverbinding en probeer het opnieuw.'
					: 'Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.',
			);
			setAltchaPayload(null);
		} finally {
			setIsSubmitting(false);
		}
	};

	// ── Success State ──
	if (isSuccess) {
		return (
			<div ref={formRef}>
				<ConsumerSuccessPage
					leadId={leadId}
					contactName={methods.getValues('contact_name')}
				/>
			</div>
		);
	}

	// ── Form State ──
	return (
		<div ref={formRef}>
			{/* Header */}
			<div className="bg-blue-dark py-4 md:py-6 lg:py-8">
				<Container>
					<div className="text-center">
						<h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-white">
							Showroom Afspraak Maken
						</h2>
						<p className="text-blue-100 mt-1 text-sm sm:text-base lg:text-lg font-medium">
							Bekijk onze airco-systemen en krijg persoonlijk advies
						</p>
					</div>
				</Container>
			</div>

			{/* Form */}
			<Container>
				<div className="max-w-2xl mx-auto py-8 md:py-12">
					<FormProvider {...methods}>
						<form
							onSubmit={handleSubmit(onSubmit, (validationErrors) => {
								trackEvent('form_validation_error', {
									form_name: 'showroom_afspraak',
									fields: Object.keys(validationErrors).join(','),
								});
							})}
							onFocusCapture={handleFormStart}
							className="space-y-8"
							noValidate
						>
							{/* Error message */}
							{error && (
								<div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">
									{error}
								</div>
							)}

							{/* Name */}
							<div className="space-y-3">
								<Label htmlFor="contact_name" className="text-lg">
									Naam <span className="text-red-500">*</span>
								</Label>
								<Input
									id="contact_name"
									placeholder="Uw volledige naam"
									className={
										errors.contact_name
											? 'border-red-500 h-12 text-lg md:text-lg'
											: 'h-12 text-lg md:text-lg'
									}
									{...register('contact_name')}
								/>
								{errors.contact_name && (
									<p className="text-sm text-red-500">{errors.contact_name.message}</p>
								)}
							</div>

							{/* Email + Phone */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<Label htmlFor="contact_email" className="text-lg">
										E-mailadres <span className="text-red-500">*</span>
									</Label>
									<Input
										id="contact_email"
										type="email"
										placeholder="uw@email.nl"
										className={
											errors.contact_email
												? 'border-red-500 h-12 text-lg md:text-lg'
												: 'h-12 text-lg md:text-lg'
										}
										{...register('contact_email')}
									/>
									{errors.contact_email && (
										<p className="text-sm text-red-500">{errors.contact_email.message}</p>
									)}
								</div>
								<div className="space-y-3">
									<Label htmlFor="contact_phone" className="text-lg">
										Telefoon <span className="text-red-500">*</span>
									</Label>
									<Input
										id="contact_phone"
										type="tel"
										placeholder="06 12345678"
										className={
											errors.contact_phone
												? 'border-red-500 h-12 text-lg md:text-lg'
												: 'h-12 text-lg md:text-lg'
										}
										{...register('contact_phone')}
									/>
									{errors.contact_phone && (
										<p className="text-sm text-red-500">{errors.contact_phone.message}</p>
									)}
								</div>
							</div>

							{/* Date + Time slot */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-3">
									<Label htmlFor="preferred_date" className="text-lg">
										Voorkeursdatum{' '}
										<span className="text-gray-400 font-normal">(optioneel)</span>
									</Label>
									<DatePicker
										id="preferred_date"
										value={preferredDate || undefined}
										onChange={(date) => setValue('preferred_date', date || '', { shouldValidate: true })}
										disableWeekends
										error={!!errors.preferred_date}
									/>
									{errors.preferred_date && (
										<p className="text-sm text-red-500">{errors.preferred_date.message}</p>
									)}
								</div>
								<div className="space-y-3">
									<Label htmlFor="preferred_time_slot" className="text-lg">
										Voorkeurstijdstip{' '}
										<span className="text-gray-400 font-normal">(optioneel)</span>
									</Label>
									<Select
										onValueChange={(value) =>
											setValue('preferred_time_slot', value as 'ochtend' | 'middag', {
												shouldValidate: true,
											})
										}
									>
										<SelectTrigger
											id="preferred_time_slot"
											className={
												errors.preferred_time_slot
													? 'border-red-500 h-12 text-lg md:text-lg'
													: 'h-12 text-lg md:text-lg'
											}
										>
											<SelectValue placeholder="Kies een tijdstip" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="ochtend">Ochtend (09:00–12:00)</SelectItem>
											<SelectItem value="middag">Middag (13:00–17:00)</SelectItem>
										</SelectContent>
									</Select>
									{errors.preferred_time_slot && (
										<p className="text-sm text-red-500">{errors.preferred_time_slot.message}</p>
									)}
								</div>
							</div>

							{/* Interest */}
							<div className="space-y-3">
								<Label htmlFor="interest" className="text-lg">
									Waar bent u in geïnteresseerd?{' '}
									<span className="text-gray-400 font-normal">(optioneel)</span>
								</Label>
								<Textarea
									id="interest"
									placeholder="Bijv. split-unit airco voor de slaapkamer, warmtepomp, of multi-split systeem..."
									className="min-h-[120px] text-lg md:text-lg p-3"
									{...register('interest')}
								/>
							</div>

							{/* Privacy checkbox */}
							<div className="flex items-start space-x-3 pt-6 border-t border-gray-200">
								<Checkbox
									id="privacy_agreed"
									checked={privacyAgreed}
									onCheckedChange={(checked) =>
										setValue('privacy_agreed', checked === true, { shouldValidate: true })
									}
									className={
										errors.privacy_agreed
											? 'border-red-500 size-5 mt-1'
											: 'size-5 mt-1 border-gray-400'
									}
								/>
								<div className="grid gap-1.5 leading-none">
									<Label
										htmlFor="privacy_agreed"
										className="text-lg font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
									>
										Ik ga akkoord met de privacyverklaring{' '}
										<span className="text-red-500">*</span>
									</Label>
									<p className="text-sm text-gray-500">
										Wij gebruiken uw gegevens alleen om contact met u op te nemen over uw
										showroombezoek.
									</p>
									{errors.privacy_agreed && (
										<p className="text-sm text-red-500">{errors.privacy_agreed.message}</p>
									)}
								</div>
							</div>

							{/* ALTCHA */}
							{!e2eBypass && <AltchaWidget onVerified={setAltchaPayload} onError={handleAltchaError} />}

							{/* Trust indicator */}
							<div className="flex items-center gap-2 text-sm font-medium text-green-700 bg-green-50 p-3 rounded-md border border-green-100">
								<ShieldCheck className="size-5" />
								<span>Gecertificeerd Airco Installateur — bezoek op afspraak</span>
							</div>

							{/* Submit */}
							<Button
								type="submit"
								disabled={Boolean(isSubmitting || (!altchaPayload && !e2eBypass))}
							suppressHydrationWarning
								className="w-full md:w-auto min-w-[200px] bg-orange hover:bg-orange-hover text-white font-bold text-lg py-6"
							>
								{isSubmitting ? (
									<>
										<Loader2 className="mr-2 size-5 animate-spin" />
										Verzenden...
									</>
								) : (
									'Afspraak Aanvragen'
								)}
							</Button>
						</form>
					</FormProvider>
				</div>
			</Container>
		</div>
	);
}

export default function ContactForm() {
	return (
		<Suspense
			fallback={
				<div className="bg-blue-dark py-4 md:py-6 lg:py-8">
					<Container>
						<div className="text-center">
							<h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-white">
								Showroom Afspraak Maken
							</h2>
						</div>
					</Container>
				</div>
			}
		>
			<ContactFormContent />
		</Suspense>
	);
}
